import Button from '../shared/components/buttons';
import { FC, useRef, useState } from 'react';
import { TextDecoder } from '../shared/text-effects/TextDecoder';
import { useTextSize } from '../shared/hooks/useTextSize';
import { ReactComponent as CopyIcon } from '../shared/components/icons/copy-icon.svg';
import { ReactComponent as EmailIcon } from '../shared/components/icons/email-icon.svg';
import { useTimeout } from 'usehooks-ts';
import clsx from 'clsx';
import { useNavigate } from 'react-router-dom';
import { PATHS } from '../shared/defines';

const WHATSAPP_SHARE_CONTENT =
  'Hi - Your child has been invited to SPYGAMES. Please complete the online waiver before their visit - https://waiver.spygames.com/sign. Please ensure they were flat-soled trainers and wear comfortable clothing as it’s quite physical!';

const EMAIL_SHARE_CONTENT =
  'Hi,\n\nYour child has been invited to SPYGAMES. Please complete the online waiver before their visit - https://waiver.spygames.com/sign\n\nAnd please ensure they were flat-soled trainers and wear comfortable clothing as it’s quite physical!';

const SHARE_URL = 'https://waiver.spygames.com/sign';
const EMAIL_SUBJECT = 'SPYGAMES waiver for your child';

const ChildWaiverContent: FC = () => {
  const isMobile = /iPhone|iPad|iPod|Android/i.test(navigator.userAgent);
  const hiddenRef = useRef<HTMLHeadingElement>(null);
  const { height: titleHeight } = useTextSize(hiddenRef);
  const subtitleHiddenRef = useRef<HTMLHeadingElement>(null);
  const { height: subtitleHeight } = useTextSize(subtitleHiddenRef);

  const navigate = useNavigate();

  const [linkCopied, setLinkCopied] = useState(false);
  const [linkCopiedBtn, setLinkCopiedBtn] = useState(false);

  useTimeout(
    () => {
      setLinkCopied(false);
    },
    linkCopied ? 1500 : null
  );

  useTimeout(
    () => {
      setLinkCopiedBtn(false);
    },
    linkCopiedBtn ? 1500 : null
  );

  const onEmailShareClick = () => {
    const mailtoUrl = `mailto:?subject=${encodeURIComponent(
      EMAIL_SUBJECT
    )}&body=${encodeURIComponent(EMAIL_SHARE_CONTENT)}`;
    window.location.href = mailtoUrl;
  };

  const onCopyClick = async (copiedCallback: (copied: boolean) => void) => {
    if (navigator.clipboard) {
      try {
        await navigator.clipboard.writeText(SHARE_URL);
        copiedCallback(true);
      } catch (error) {
        console.error('Failed to copy text:', error);
      }
    } else {
      const textArea = document.createElement('textarea');
      textArea.value = SHARE_URL;
      document.body.appendChild(textArea);
      textArea.focus();
      textArea.select();
      try {
        document.execCommand('copy');
        copiedCallback(true);
      } catch (error) {
        console.error('Failed to copy text:', error);
      }
      document.body.removeChild(textArea);
    }
  };

  const handleShare = async () => {
    if (isMobile && navigator.share) {
      try {
        await navigator.share({
          title: 'Check this out!',
          text: WHATSAPP_SHARE_CONTENT,
        });
      } catch (error) {
        console.error('Error sharing content:', error);
      }
    }
  };

  const handleSignWaiver = () => {
    navigate(`/${PATHS.SIGN}`);
  };

  return (
    <>
      <div className="flex flex-col gap-2 sm:gap-0">
        <div
          className="whitespace-normal w-full"
          style={{ height: `${titleHeight}px` }}
        >
          <TextDecoder
            className="font-aeonik font-medium sm:text-[3.1rem]/[3.4rem] text-[2.125rem]/[2.125rem] gap-5"
            delay={0}
            duration={300}
            animationType={'in'}
            text="Important:"
          />
        </div>
        <h1
          className="absolute opacity-0 top-0 pointer-events-none font-aeonik font-medium sm:text-[3.1rem]/[3.4rem] text-[2.125rem]/[2.125rem]"
          ref={hiddenRef}
        >
          Important:
        </h1>
        <div
          className="whitespace-normal w-full"
          style={{ height: `${subtitleHeight}px` }}
        >
          <TextDecoder
            className="font-aeonik font-medium sm:text-[3.1rem]/[3.4rem] text-[2.125rem]/[2.125rem] gap-5"
            delay={300}
            duration={500}
            animationType={'in'}
            text="Child waivers needed"
          />
        </div>
        <h1
          className="absolute opacity-0 top-0 pointer-events-none font-aeonik font-medium sm:text-[3.1rem]/[3.4rem] text-[2.125rem]/[2.125rem]"
          ref={subtitleHiddenRef}
        >
          Child waivers needed
        </h1>
      </div>
      <div className="text-[1.25rem]/[1.5rem] sm:text-[1.875rem]/[1.875rem]">
        A waiver must be signed for each child ahead of their visit.
      </div>

      <div className="flex flex-col gap-5">
        <div className="flex flex-col px-3.5 sm:px-7.5 py-5 sm:py-7.5 bg-green-forest rounded-2">
          <div className="text-[1.25rem]/[1.5rem] sm:text-[1.875rem]/[1.875rem] font-medium sm:border-white border-transparent sm:text-start">
            Step 1: For your guests
          </div>
          {isMobile && (
            <div
              className={clsx(
                'sm:text-[1.125rem]/[1.5rem] text-[0.875rem]/[1.0625rem] mt-5 sm:mt-7.5 pb-5 sm:pb-2.5 font-medium sm:border-white sm:text-start',
                {
                  'border-b-0 sm:border-b-1': !isMobile,
                }
              )}
            >
              Share the waiver link with other parents/guardians
            </div>
          )}
          {!isMobile && (
            <div
              className={clsx(
                'sm:text-[1.125rem]/[1.5rem] text-[0.875rem]/[1.0625rem] mt-5 sm:mt-7.5 pb-5 sm:pb-2.5 border-b-0 sm:border-b-1  font-medium sm:border-white sm:text-start'
              )}
            >
              Choose a method to share the waiver link with other
              parents/guardians
            </div>
          )}

          {!isMobile && (
            <>
              <div className="w-full flex flex-col mt-5 sm:flex-row gap-2.5 relative">
                <div
                  className={clsx(
                    'bg-green-dark rounded-lg h-12 sm:text-[1.125rem]/[1.5rem] text-green-laser border-green-laser border-2 font-aeonik px-3.5 font-medium flex items-center w-full relative cursor-pointer'
                  )}
                  onClick={() => {
                    onCopyClick(setLinkCopied);
                  }}
                >
                  <div
                    className={clsx(
                      'overflow-hidden transition-all whitespace-nowrap overflow-ellipsis text-[0.875rem]/[1.0625rem] sm:text-[1.125rem]/[1.5rem] font-aeonik font-medium',
                      !linkCopied ? 'opacity-100' : 'opacity-0'
                    )}
                  >
                    {SHARE_URL.replace(/^https?:\/\//, '')}
                  </div>
                  <button
                    className={clsx(
                      'absolute h-full w-10 transition-all right-0 flex justify-center items-center active:opacity-50',
                      !linkCopied ? 'opacity-100' : 'opacity-0'
                    )}
                    onClick={() => {
                      onCopyClick(setLinkCopied);
                    }}
                  >
                    <CopyIcon className="w-4 h-4 text-green-laser" />
                  </button>
                </div>
                <div
                  className={clsx(
                    'pointer-events-none transition-all w-full absolute z-50 bg-green-laser rounded-lg h-12 text-[0.875rem]/[1.0625rem] sm:text-[1.125rem]/[1.5rem] text-black  font-aeonik px-3.5 font-medium flex items-center justify-center',
                    linkCopied ? 'opacity-100' : 'opacity-0'
                  )}
                  style={{ transitionDuration: '0.5s' }}
                >
                  Link copied
                </div>
              </div>

              <div className="w-full h-11 flex items-center justify-center">
                or
              </div>

              <div className="w-full flex gap-2.5 sm:flex-row flex-col">
                <Button
                  variant="filled"
                  size="sm"
                  className="sm:flex-1 flex-grow-0 flex-shrink-0 px-0 flex gap-2 justify-center"
                  onClick={() => {
                    onCopyClick(setLinkCopiedBtn);
                  }}
                >
                  <CopyIcon className="w-4 h-4 text-green-dark" />
                  <span className="text-[1.1rem]">
                    {linkCopiedBtn ? 'Copied!' : 'Copy link'}
                  </span>
                </Button>

                <Button
                  variant="filled"
                  size="sm"
                  className="sm:flex-1 flex-grow-0 flex-shrink-0 flex px-0 gap-2 justify-center"
                  onClick={onEmailShareClick}
                >
                  <EmailIcon className="w-5.5 h-5.5" />
                  <span className="text-[1.1rem]">Share via Email</span>
                </Button>
              </div>
            </>
          )}
          {isMobile && (
            <Button
              variant="filled"
              size="sm"
              className="text-[1.2rem]"
              onClick={handleShare}
            >
              Share the waiver link
            </Button>
          )}
        </div>

        <div className="flex flex-col px-3.5 sm:px-7.5 py-5 sm:py-7.5 bg-green-forest rounded-2">
          <div className="text-[1.25rem]/[1.5rem] mb-5 sm:mb-7.5 sm:text-[1.875rem]/[1.875rem] font-medium sm:border-white border-transparent sm:text-start">
            Step 2: For your children
          </div>
          <Button
            variant="filled"
            size="sm"
            className="text-[1.2rem]"
            onClick={handleSignWaiver}
          >
            Sign a waiver
          </Button>
        </div>
      </div>
    </>
  );
};

export default ChildWaiverContent;
